import { useEffect, useState } from 'react';
import { PlannerEnv } from '@/utils/env';
import { Backup } from '@/services/configurator/Backup';
import { Production } from '@/services/configurator/Production';
import { PlannerRestoreDialog } from './planner-restore-dialog';
import { useEnv } from '@/components/providers/env-provider';
import { PlannerConfigEntity } from '@/services/configurator/PlannerConfigTypes';
import { DataTable } from '../backup-list/data-table';
import { columns } from '../backup-list/columns';
import PlannerDiffDialog from './planner-diff-dialog';

export type PlannerRestoreFromBackupList = {
  backupId: string;
  identifier: string;
  backupTime: string;
  modifiedBy: string;
};

const LIMIT = 10;

export function PlannerRestoreList() {
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const { env } = useEnv();
  const [backupList, setBackupList] = useState<PlannerRestoreFromBackupList[]>(
    [],
  );
  const [prev, setPrev] = useState<PlannerConfigEntity | null>(null);
  const [current, setCurrent] = useState<PlannerConfigEntity | null>(null);
  const [showDiff, setShowDiff] = useState<string>('');
  const [restoreDialogData, setRestoreDialogData] =
    useState<PlannerRestoreFromBackupList>({} as PlannerRestoreFromBackupList);

  const getBackupData = async (
    envParam: PlannerEnv,
    offsetParam: number,
    searchParam: string,
  ) => {
    new Backup(envParam).getList(offsetParam, 10, searchParam).then((data) => {
      const list = data.reduce((acc, item) => {
        acc.push({
          backupId: item.backupId,
          identifier: item.identifier,
          backupTime: item.backupTime,
          modifiedBy: item.modifiedBy,
        });
        return acc;
      }, [] as PlannerRestoreFromBackupList[]);
      setBackupList(list);
    });
  };

  useEffect(() => {
    setOffset(0);
  }, [search]);

  useEffect(() => {
    getBackupData(env, offset, search);
  }, [env, offset, search]);

  useEffect(() => {
    if (!showDiff) {
      return;
    }
    const backupIdentifier = backupList.find(
      (planner) => planner.backupId === showDiff,
    )?.identifier;

    if (backupIdentifier) {
      Promise.all([
        new Backup(env).getSingleBackup(showDiff),
        new Production(env).getSinglePlanner(backupIdentifier),
      ]).then(([backup, production]) => {
        if (backup) {
          if (production) {
            setPrev(production);
            setCurrent(backup);
          }
        }
      });
    }
  }, [showDiff, backupList, env]);

  const onNextPage = () => {
    setOffset((prev) => {
      return prev + LIMIT;
    });
  };

  const onPreviousPage = () => {
    setOffset((prev) => {
      const newOffset = prev - LIMIT;
      return newOffset < 0 ? 0 : newOffset;
    });
  };

  return (
    <div className='pe-4'>
      <DataTable
        columns={columns(setShowDiff, setRestoreDialogData)}
        data={backupList}
        onNextPage={onNextPage}
        onPreviousPage={onPreviousPage}
        nextPageDisabled={backupList.length < LIMIT}
        previousPageDisabled={offset <= 0}
        onSearch={setSearch}
      />
      <PlannerDiffDialog
        showDiff={showDiff}
        setShowDiff={setShowDiff}
        setPrev={setPrev}
        setCurrent={setCurrent}
        prev={prev}
        current={current}
      />
      <PlannerRestoreDialog
        name={restoreDialogData?.identifier}
        backupId={restoreDialogData?.backupId}
        open={restoreDialogData}
        setRestoreDialogData={setRestoreDialogData}
        onRestore={async () => {
          await getBackupData(env, 0, '');
        }}
      />
    </div>
  );
}
